/**
 * Defect-related models for the Angular 21 app.
 * Migrated from web-app-legacy/js/app/Models/Contracts.ts
 */

// Global location coordinates
export interface IGlobalLocation {
  x: number;
  y: number;
  z: number;
}

// POI location as used by backend
export interface SkomapLocation {
  lon: number;
  lat: number;
  z: number;
}

// Skomap POI for creating defects
export interface ISkomapPoi {
  Id: number;
  Importance: number;
  Location: SkomapLocation;
}

// Main defect interface
export interface IDefect {
  id: number;
  buildingId: number;
  title: string;
  description: string;
  severityLevelId: number;
  category1Id: number;
  category2Id: number;
  category3Id: number;
  poiId: number;
  createdBy: string;
  createdByPersonalNumber: string;
  createdAt: number;
  lastUpdateAt: number;
  contactEmail: string;
  contactPhone: string;
  defectStateId: number;
  reportToHelpdeskAt: number;
  troubleshooter: string;
  linkedPoiId: number;
  coordinator: IEmployee;
  managers: IEmployee[];
  location: SkomapLocation;
  siteId: number;
  couldBeReportedToSap: boolean;
  reportToSapStatus: number;
}

// Defect class for creating new defects
export class Defect implements IDefect {
  id = 0;
  buildingId = 0;
  title = '';
  description = '';
  severityLevelId = 0;
  category1Id = 0;
  category2Id = 0;
  category3Id = 0;
  poiId = 0;
  createdBy = '';
  createdByPersonalNumber = '';
  createdAt = 0;
  lastUpdateAt = 0;
  contactEmail = '';
  contactPhone = '';
  defectStateId = 1; // Default: Reported
  reportToHelpdeskAt = 0;
  troubleshooter = '';
  linkedPoiId = 0;
  coordinator: IEmployee = null!;
  managers: IEmployee[] = [];
  location: SkomapLocation = { lon: 0, lat: 0, z: 0 };
  siteId = 0;
  couldBeReportedToSap = false;
  reportToSapStatus = 0;
}

// Wrapper model with POI data for API calls
export interface IDefectModel {
  poi: ISkomapPoi;
  defect: IDefect;
}

// Severity level codebook
export interface ISeverityLevel {
  id: number;
  title: string;
  icon?: string;
}

// Defect state codebook
export interface IDefectState {
  id: number;
  title: string;
  icon?: string;
}

// Category level 1
export interface ICategory {
  id: number;
  title: string;
  order: string;
  icon: string;
  isOutdoor: boolean;
}

// Category level 2
export interface ICategory2 extends ICategory {
  category1Id: number;
}

// Category level 3
export interface ICategory3 extends ICategory {
  category2Id: number;
  isDescriptionRequered: boolean; // Note: typo from backend
}

// Employee information
export interface IEmployee {
  personalNumber: string;
  functionalAreaId: number;
  departmentId: number;
  departmentName: string;
  titleBefore: string;
  name: string;
  surname: string;
  titleAfter: string;
  fullName: string;
  email: string;
  phone: string;
  isExternal: boolean;
  isBuildingManager: boolean;
}

// Building manager assignment
export interface IBuildingManager {
  buildingId: number;
  manager: IEmployee;
  coordinator: IEmployee;
}

// Defect picture
export interface IDefectPicture {
  id: string;
  defectId: string;
}

export interface IDefectPictureList {
  items: IDefectPicture[];
}

// Event data for report defect context menu
export interface ReportDefectEventData {
  globalLocation: IGlobalLocation;
  poiId?: number;
}

// Defect location info (building + managers)
export interface DefectLocationInfo {
  id: number;
  name: string;
  description?: string;
  managers: IEmployee[];
  coordinator: IEmployee;
  isOutdoorLocation: boolean;
}

// User info from middleware settings
export interface IUserInfo {
  employee: IEmployee;
  roles: string[];
}

// Defect state enum
export enum DefectStateEnum {
  Reported = 1,
  InProgress = 2,
  Solved = 3,
  Refused = 4,
}

// Image view model for the editor
export interface IImageVm {
  id: number;
  file: File | null;
  guid: string | null;
  name: string;
  isUploaded: boolean;
  isLoaded: boolean;
  src: string | null;
}
